import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  GridComponent,
  GridDataResult,
  PageChangeEvent,
  SelectableSettings,
  SelectAllCheckboxState,
} from '@progress/kendo-angular-grid';
import { BaseComponent } from '@stockaid/core';
import { first, tap } from 'rxjs/operators';
import { Item } from 'src/app/core/models/item';
import {
  ApplyRecommendationPayload,
  Recommendation,
  RecommendationEntityType,
  RecommendationType,
} from 'src/app/core/models/recommendation';
import { RecommendationStatus, RecommendationValueState } from 'src/app/core/infrastructure/enums/meta-data-field.enum';
import { ItemPriceState } from 'src/app/core/models/history-log';
import { RecommendationsService } from 'src/app/core/services/recommendation.service';
import { MODAL_DATA } from '@stockaid/services';
import { CompanyService } from 'src/app/core/services/company.service';
import { SnotifyService } from 'ng-snotify';
import {
  buildRepricingItem,
  computeNewMinMax,
  getDerivedSalePrice,
  getRepricingEffectiveTrend,
  RepricingItem,
  validateDerivedSalePrice,
  validateRepricingBounds,
  validateRepricingPrice,
} from 'src/app/core/utils/recommendation.utils';
import { amazonProductLink } from 'src/app/core/constants/amazon-link.constant';
import { Observable, forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

interface RepricingItemSelection extends RepricingItem {
  id: string;
  sku: string;
  asin: string;
  skuLink: string;
  isSelected: boolean;
}

interface RepricingRowEditState {
  // Because we having upload custom price from CSV, we have been updated the custom price to recommendedOutput.customPrice
  // In case the popup opened, and user edit the custom price again, we need to compare it changed
  originalCustomPrice: number;
  customPrice: number;
  isEditing: boolean;
  validationError: string | null;
  derivedSalePrice: number;
  effectiveTrend: number;
  /** Live bounds — recomputed from activePrice on every change */
  newMin: number | undefined;
  newMax: number | undefined;
  /** User-entered custom min/max overrides — null means not set by user */
  customMin: number | null;
  isCustomMinTouched?: boolean;
  customMax: number | null;
  isCustomMaxTouched?: boolean;
  isEditingMin: boolean;
  isEditingMax: boolean;
  minValidationError: string | null;
  maxValidationError: string | null;
}

interface RepricingBulkSettingModalInput {
  items: Item[];
  displayCurrency: { code: string; symbol: string };
  /** Page size inherited from the parent Recommendations grid */
  pageSize?: number;
  /** Inverse Selection fields */
  selectAll?: boolean;
  excludedIds?: string[];
  filters?: any;
  /** When set, the modal was opened from a CSV price upload. Items are pre-filtered by this batch. */
  uploadBatchId?: string;
}
@Component({
  selector: 'app-repricing-bulk-setting-modal',
  templateUrl: './repricing-bulk-setting-modal.component.html',
  styleUrls: ['./repricing-bulk-setting-modal.component.scss'],
})
export class RepricingBulkSettingModalComponent
  extends BaseComponent
  implements OnInit {
  get displayCurrency() {
    return this?.data?.displayCurrency;
  }

  public gridData: RepricingItemSelection[] = [];
  public expandedDetailKeys: string[] = [];

  // Pagination — page size is inherited from the parent grid at open time (fallback: 100)
  public pageSize = 100;
  public skip = 0;
  public totalCount = 0;

  public expandDetailsBy = (dataItem: RepricingItemSelection): string => {
    return dataItem.id;
  };

  isLoading = true;
  isSubmitting = false;
  /** Tracks whether Sync to AMZ completed successfully — prevents revert on normal close */
  private hasSynced = false;

  /** When true, shows ALL items (changed + unchanged). When false, only changed items are fetched. */
  isShownRecommendationChanged = false;

  /** The current page data bound directly to the grid */
  shownGridData: GridDataResult = { data: [], total: 0 };

  // Config Selection for Kendo Grid
  public selectableSettings: SelectableSettings = {
    checkboxOnly: true,
    mode: 'multiple',
  };

  selectedItemKeys: string[] = [];
  editState: Record<string, RepricingRowEditState> = {};

  /**
   * Mode 1 (selectAll=true):  tracks which items the user explicitly UNCHECKED.
   * Mode 2 (selectAll=false): not used — selectedItemKeys drives everything.
   */
  excludedKeys = new Set<string>();

  mappingRecommendations!: Map<string, Recommendation>;

  @ViewChild(GridComponent) grid: GridComponent;
  readonly RecommendationStatus = RecommendationStatus;
  readonly RecommendationValueState = RecommendationValueState;
  readonly ItemPriceState = ItemPriceState;

  public rowClass = (args) => ({
    'k-disabled': [RecommendationStatus.QUEUED, RecommendationStatus.IN_PROGRESS, RecommendationStatus.APPLIED].includes(
      args.dataItem.status,
    ),
  });

  /**
   * Header checkbox state — evaluated against the current page rows.
   *
   * Mode 1 (selectAll): checked when no items on this page are excluded.
   * Mode 2 (explicit):  checked when all items on this page are in selectedItemKeys.
   */
  public get selectAllState(): SelectAllCheckboxState {
    const pageKeys = this.gridData.map((i) => i.id);
    if (!pageKeys.length) return 'unchecked';

    if (this.data.selectAll) {
      const excludedOnPage = pageKeys.filter((k) => this.excludedKeys.has(k));
      if (excludedOnPage.length === 0) return 'checked';
      if (excludedOnPage.length === pageKeys.length) return 'unchecked';
      return 'indeterminate';
    }

    const selectedSet = new Set(this.selectedItemKeys);
    const selectedOnPage = pageKeys.filter((k) => selectedSet.has(k));
    if (selectedOnPage.length === 0) return 'unchecked';
    if (selectedOnPage.length === pageKeys.length) return 'checked';
    return 'indeterminate';
  }
  /**
   * The keys Kendo Grid uses to render individual row checkboxes as checked.
   *
   * Mode 1 (selectAll): all page-item IDs except those in excludedKeys.
   * Mode 2 (explicit):  intersection of selectedItemKeys with the current page.
   */
    public get gridSelectedKeys(): string[] {
    const pageIds = this.gridData.map((i) => i.id);
    
    // 1. Get the selected keys for the current page
    const pageSelectedKeys = this.data.selectAll
      ? pageIds.filter((id) => !this.excludedKeys.has(id))
      : pageIds.filter((id) => this.selectedItemKeys.includes(id));

    // 2. Determine the global count
    const totalSelectedCount = this.data.selectAll
      ? this.totalCount - this.excludedKeys.size
      : this.selectedItemKeys.length;

    // 3. Extend the array length to match the global count
    const result = [...pageSelectedKeys];
    if (result.length < totalSelectedCount) {
      result.length = totalSelectedCount;
    }
    
    return result;
  }


  constructor(
    public activeModal: NgbActiveModal,
    public recommendationsService: RecommendationsService,
    public companyService: CompanyService,
    private snotifyService: SnotifyService,
    @Inject(MODAL_DATA) public data: RepricingBulkSettingModalInput
  ) {
    super();
  }

  get currentCompany() {
    return this.companyService.currentCompany();
  }

  ngOnInit(): void {
    // Inherit the page size from the parent grid (e.g. Recommendations page)
    this.pageSize = this.data.pageSize ?? 100;

    if (this.data.uploadBatchId) {
      // Upload batch mode: show all items in the batch (skip valueState filter)
      this.isShownRecommendationChanged = false;
    } else {
      this.selectedItemKeys = this.data.items.map(i => i.key);

      // Seed excludedIds from the parent (inverse-selection mode)
      if (this.data.selectAll && this.data.excludedIds?.length) {
        this.data.excludedIds.forEach(k => this.excludedKeys.add(k));
      }
    }

    // Fetch data and total count in parallel on initial load
    forkJoin([
      this.fetchRecommendationItemPrices(),
      this.fetchRecommendationItemCount(),
    ])
      .pipe(this.autoCleanUp())
      .subscribe(() => {
        this.shownGridData = { data: this.gridData, total: this.totalCount };
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    // Revert the upload batch if the user closed without syncing
    if (this.data.uploadBatchId && !this.hasSynced) {
      this.recommendationsService.revertCsvUpload(this.data.uploadBatchId).subscribe();
    }
  }

  /**
   * Header checkbox clicked.
   * Mode 1: toggle excludedKeys on the current page.
   * Mode 2: toggle selectedItemKeys on the current page.
   */
  public onSelectAllChange(checkedState: SelectAllCheckboxState): void {
    const pageKeys = this.gridData.map((i) => i.id);

    if (this.data.selectAll) {
      if (checkedState === 'checked') {
        pageKeys.forEach((k) => this.excludedKeys.delete(k));
      } else {
        pageKeys.forEach((k) => this.excludedKeys.add(k));
      }
    } else {
      const selectedSet = new Set(this.selectedItemKeys);
      if (checkedState === 'checked') {
        pageKeys.forEach((k) => selectedSet.add(k));
      } else {
        pageKeys.forEach((k) => selectedSet.delete(k));
      }
      this.selectedItemKeys = Array.from(selectedSet);
    }
  }

  /**
   * Individual row selected / deselected.
   * Mode 1: add/remove from excludedKeys.
   * Mode 2: add/remove from selectedItemKeys.
   */
  public onSelectionChange(event): void {
    if (this.data.selectAll) {
      if (event.deselectedRows) {
        event.deselectedRows.forEach((row) =>
          this.excludedKeys.add(row.dataItem.id)
        );
      }
      if (event.selectedRows) {
        event.selectedRows.forEach((row) =>
          this.excludedKeys.delete(row.dataItem.id)
        );
      }
    } else {
      const selectedSet = new Set(this.selectedItemKeys);
      if (event.selectedRows) {
        event.selectedRows.forEach((row) => selectedSet.add(row.dataItem.id));
      }
      if (event.deselectedRows) {
        event.deselectedRows.forEach((row) => selectedSet.delete(row.dataItem.id));
      }
      this.selectedItemKeys = Array.from(selectedSet);
    }
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.isLoading = true;
    // On page change, only re-fetch the page data — count stays the same
    this.fetchRecommendationItemPrices()
      .pipe(this.autoCleanUp())
      .subscribe(() => {
        this.shownGridData = { data: this.gridData, total: this.totalCount };
        this.isLoading = false;
      });
  }

  toggleShownUnchanged(): void {
    this.isShownRecommendationChanged = !this.isShownRecommendationChanged;
    // Reset to first page and re-fetch — the filter changes server-side
    this.skip = 0;
    this.isLoading = true;
    forkJoin([
      this.fetchRecommendationItemPrices(),
      this.fetchRecommendationItemCount(),
    ])
      .pipe(this.autoCleanUp())
      .subscribe(() => {
        this.shownGridData = { data: this.gridData, total: this.totalCount };
        this.isLoading = false;
      });
  }

  private fetchRecommendationItemPrices(): Observable<any> {
    return this.recommendationsService
      .getFilteredUserTelerikEvent(
        this.skip,
        this.pageSize,
        this.buildFilter(),
        undefined,
        '',
        undefined,
        {
          currencyCode: this.data.displayCurrency.code,
          marketplaceId: this.currentCompany.marketplaceId,
        }
      )
      .pipe(
        tap((recommendations: Recommendation[]) => {
          this.mappingRecommendations = new Map<string, Recommendation>(
            recommendations.map((i) => [i.entityKey, i])
          );

          this.gridData = recommendations.map((r) => this.buildRepricingItemSelection(r));

          // On first load, pre-select all items on this page
          // if (this.skip === 0 && this._selectedIds.size === 0) {
          //   this.gridData.forEach((i) => this._selectedIds.add(i.id));
          // }
        })
      );
  }

  /**
   * Fetches the total record count for the current filter.
   * Called once on init — count does not change between page navigations.
  **/

  private fetchRecommendationItemCount(): Observable<number> {
    return this.recommendationsService.getCount(
      this.buildFilter(),
      '',
      undefined,
      undefined,
      {
        isRefreshed: true
      }
    ).pipe(tap((count) => this.totalCount = count));
  }

  private buildRepricingItemSelection(
    recommendation: Recommendation
  ): RepricingItemSelection {
    const entityKey = recommendation.entityKey;
    const firstHyphenIndex = entityKey.indexOf("-");
    if (firstHyphenIndex === -1) {
      throw new Error(`Invalid entity key: ${entityKey}`);
    }
    const asin = entityKey.substring(0, firstHyphenIndex);
    const sku = entityKey.substring(firstHyphenIndex + 1);

    const base = buildRepricingItem(recommendation);
    const id = recommendation.entityKey;

    // Initialise editState only once — preserves overrides across pagination
    if (!this.editState[id]) {
      const customPrice = recommendation?.recommendationOutput?.customPrice ?? base.newInboundPrice;
      const { newMin, newMax } = computeNewMinMax(
        customPrice,
        base.suggestedMin,
        base.suggestedMax,
        base.currentMin,
        base.currentMax,
      );
      this.editState[id] = {
        originalCustomPrice: customPrice,
        customPrice,
        isEditing: false,
        validationError: null,
        derivedSalePrice: getDerivedSalePrice(customPrice, base.currentSalePrice, base.currentInboundPrice),
        effectiveTrend: getRepricingEffectiveTrend(customPrice, base.currentInboundPrice, base.trendPercentage, base.newInboundPrice),
        newMin,
        newMax,
        customMin: base.customMinPrice != null ? base.customMinPrice : (base.suggestedMin != null ? base.suggestedMin : null),
        customMax: base.customMaxPrice != null ? base.customMaxPrice : (base.suggestedMax != null ? base.suggestedMax : null),
        isEditingMin: false,
        isEditingMax: false,
        minValidationError: null,
        maxValidationError: null,
      };
    }

    return {
      ...base,
      id,
      sku,
      asin,
      skuLink: amazonProductLink(asin, this.currentCompany?.marketplaceId),
      isSelected: true,
    };
  }

  public getDisplayCurrency(value?: number) {
    if (value == null) return 'N/A';
    return this.displayCurrency?.symbol + value;
  }

  public openOverride(dataItem: RepricingItemSelection): void {
    this.editState[dataItem.id].isEditing = true;
  }

  public commitOverride(dataItem: RepricingItemSelection): void {
    this.editState[dataItem.id].isEditing = false;
  }

  public resetOverride(dataItem: RepricingItemSelection): void {
    const state = this.editState[dataItem.id];
    state.customPrice = dataItem.newInboundPrice;
    state.isEditing = false;
    state.validationError = null;
    state.derivedSalePrice = getDerivedSalePrice(state.customPrice, dataItem.currentSalePrice, dataItem.currentInboundPrice);
    state.effectiveTrend = getRepricingEffectiveTrend(state.customPrice, dataItem.currentInboundPrice, dataItem.trendPercentage, dataItem.newInboundPrice);
    const { newMin, newMax } = computeNewMinMax(
      state.customPrice,
      dataItem.suggestedMin,
      dataItem.suggestedMax,
      dataItem.currentMin,
      dataItem.currentMax,
    );
    state.newMin = newMin;
    state.newMax = newMax;
  }

  private revalidateBounds(id: string, inboundPrice: number, currentMax: number | null | undefined): void {
    const state = this.editState[id];
    const { minError, maxError } = validateRepricingBounds(
      state.customMin, state.customMax, state.customPrice, inboundPrice,
      (v) => this.getDisplayCurrency(v),
      currentMax ?? null,
    );
    state.minValidationError = minError;
    state.maxValidationError = maxError;
  }

  public openEditMin(id: string): void { this.editState[id].isEditingMin = true; }
  public commitEditMin(id: string): void { this.editState[id].isEditingMin = false; }
  public resetCustomMin(id: string, dataItem: RepricingItemSelection): void {
    const state = this.editState[id];
    state.customMin = dataItem.suggestedMin != null ? dataItem.suggestedMin : dataItem.currentMin;
    state.isCustomMinTouched = false;
    state.isEditingMin = false;
    this.revalidateBounds(id, dataItem.currentInboundPrice, dataItem.currentMax);
  }
  
  public onMinChange(val: number | null, id: string, dataItem: RepricingItemSelection): void {
    this.editState[id] = {
      ...this.editState[id],
      customMin: val,
      isCustomMinTouched: true,
    };
    this.revalidateBounds(id, dataItem.currentInboundPrice, dataItem.currentMax);
  }

  public openEditMax(id: string): void { this.editState[id].isEditingMax = true; }
  public commitEditMax(id: string): void { this.editState[id].isEditingMax = false; }
  public resetCustomMax(id: string, dataItem: RepricingItemSelection): void {
    const state = this.editState[id];
    state.customMax = dataItem.suggestedMax != null ? dataItem.suggestedMax : dataItem.currentMax;
    state.isCustomMaxTouched = false;
    state.isEditingMax = false;
    this.revalidateBounds(id, dataItem.currentInboundPrice, dataItem.currentMax);
  }
  public onMaxChange(val: number | null, id: string, dataItem: RepricingItemSelection): void {
    this.editState[id] = {
      ...this.editState[id],
      customMax: val,
      isCustomMaxTouched: true,
    };
    this.revalidateBounds(id, dataItem.currentInboundPrice, dataItem.currentMax);
  }

  public onPriceChange(newVal: number, dataItem: RepricingItemSelection): void {
    const state = this.editState[dataItem.id];
    state.customPrice = newVal;
    const { newMin, newMax } = computeNewMinMax(
      newVal,
      dataItem.suggestedMin,
      dataItem.suggestedMax,
      dataItem.currentMin,
      dataItem.currentMax,
    );

    state.newMin = newMin;
    state.newMax = newMax;
    this.revalidateBounds(dataItem.id, dataItem.currentInboundPrice, dataItem.currentMax);
    state.validationError = validateRepricingPrice(
      newVal,
      { min: state.newMin, max: state.newMax, listPrice: dataItem.currentInboundPrice },
      (v) => this.getDisplayCurrency(v)
    );
    state.derivedSalePrice = getDerivedSalePrice(newVal, dataItem.currentSalePrice, dataItem.currentInboundPrice);
    if (!state.validationError && dataItem.isSaleActive) {
      state.validationError = validateDerivedSalePrice(
        state.derivedSalePrice,
        { min: state.newMin, max: state.newMax },
        (v) => this.getDisplayCurrency(v)
      );
    }
    state.effectiveTrend = getRepricingEffectiveTrend(newVal, dataItem.currentInboundPrice, dataItem.trendPercentage, dataItem.newInboundPrice);
  }

  public get hasAnyValidationError(): boolean {
    const selectedSet = new Set(this.gridSelectedKeys);
    return this.gridData
      .filter(row => selectedSet.has(row.id))
      .some(row => {
        const state = this.editState[row.id];
        return (state?.isEditing && state?.validationError != null)
          || state?.minValidationError != null
          || state?.maxValidationError != null;
      });
  }

  // public get missingPriceCount(): number {
  //   return this.gridData.filter(item => item.state === ItemPriceState.MISSING_PRICE).length;
  // }

  public get hasSelectedMissingPriceWithoutEntry(): boolean {
    const selectedSet = new Set(this.gridSelectedKeys);
    return this.gridData
      .filter(row => selectedSet.has(row.id))
      .some(row => row.state === ItemPriceState.MISSING_PRICE && !this.editState[row.id]?.customPrice);
  }

  private buildFilter() {
    const commonFilters = [
      {
        filters: [
          {
            field: 'entityType',
            operator: 'eq',
            value: RecommendationEntityType.ITEM,
          },
        ],
        logic: 'and',
      },
      {
        filters: [
          { field: 'type', operator: 'eq', value: RecommendationType.PRICE },
        ],
        logic: 'and',
      },
      {
        filters: [
          {
            field: 'status',
            operator: 'eq',
            value: RecommendationStatus.NONE,
          },
          {
            field: 'status',
            operator: 'eq',
            value: RecommendationStatus.DONE,
          },
          {
            field: 'status',
            operator: 'eq',
            value: RecommendationStatus.FAILED,
          },
        ],
        logic: 'or',
      },
      ...(this.isShownRecommendationChanged
        ? [
            {
              filters: [
                {
                  field: 'valueState',
                  operator: 'eq',
                  value: 'CHANGED',
                },
              ],
              logic: 'and',
            },
          ]
        : []),
    ];

    if (this.data.uploadBatchId) {
      // Upload batch mode: filter by uploadBatchId — shows all items from this CSV upload
      return {
        logic: 'and',
        filters: [
          ...commonFilters,
          {
            filters: [{ field: 'uploadBatchId', operator: 'eq', value: this.data.uploadBatchId }],
            logic: 'and',
          },
        ],
      };
    }

    if (this.data.selectAll) {
      // Inverse Selection mode: use the parent grid's active filters.
      // Excluded IDs are embedded as a 'notin' filter on entityKey.
      const baseFilters = this.data.filters?.filters ?? [];
      const excludedIds = this.data.excludedIds ?? [];

      return {
        logic: 'and',
        filters: [
          ...commonFilters,
          // Exclude the IDs the user explicitly unchecked
          ...(excludedIds.length > 0
            ? [
                {
                  filters: [
                    {
                      field: 'entityKey',
                      operator: 'notin',
                      value: excludedIds,
                    },
                  ],
                  logic: 'and',
                },
              ]
            : []),
          ...baseFilters,
        ],
      };
    }

    // Explicit selection mode: scope to the exact set of selected item keys
    return {
      logic: 'and',
      filters: [
        ...commonFilters,
        {
          filters: [
            {
              field: 'entityKey',
              operator: 'in',
              value: this.selectedItemKeys,
            },
          ],
          logic: 'and',
        },
      ],
    };
  }

  // Helper check value
  public hasValue(val: any): boolean {
    return val !== null && val !== undefined;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if(Swal.isVisible()) return;
    this.onDismiss();
  }

  public onDismiss(): void {
    if (!this.data.uploadBatchId) {
      this.activeModal.dismiss('Cross click');
      return;
    }

    Swal.fire({
      title: 'Discard uploaded prices?',
      html: `<div>Closing this will revert all prices from your CSV upload. This action cannot be undone.</div>`,
      type: 'warning',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes, discard',
      cancelButtonText: 'Go back',
      allowOutsideClick: false,
      customClass: {
        container: 'swal-highest-popup',
      },
    }).then((result) => {
      if (!result.dismiss) {
        this.activeModal.dismiss('Cross click');
      }
    });
  }

  public onSync(e: Event) {
    const hasSelection = this.data.selectAll
      ? true                            // always has "something" selected (excluding the excluded)
      : this.selectedItemKeys.length > 0;

    if (this.isSubmitting || this.isLoading || !hasSelection || this.hasAnyValidationError) {
      return;
    }

    const selectedCount = this.data.selectAll
      ? this.totalCount - this.excludedKeys.size
      : this.selectedItemKeys.length;

    Swal.fire({
      title: 'Confirm Price Update',
      html: `<div style="text-align:left;font-size:14px;">
        <p style="margin-bottom:8px;">The following changes will be synced to Amazon for <strong>${selectedCount} SKU${selectedCount !== 1 ? 's' : ''}</strong>:</p>
        <ul style="margin:0 0 8px 0;padding-left:18px;color:#555;">
          <li>Prices overridden individually will use your custom values.</li>
          <li>SKUs without a price override will use the system suggested price.</li>
          <li>Min/Max overrides will be applied where set.</li>
        </ul>
        <p style="margin:0;color:#888;font-size:12px;">This action will update the listings on Amazon Seller Central.</p>
      </div>`,
      type: 'warning',
      showCloseButton: true,
      showCancelButton: true,
      allowOutsideClick: false,
      customClass: {
        container: 'swal-highest-popup',
      },
    }).then((willCancel) => {
      if (!willCancel.dismiss) {
        this.isSubmitting = true;

        const activeOverrides: Record<string, number> = Object.entries(this.editState)
          .filter(([, state]) => state.customPrice !== state.originalCustomPrice)
          .map(([key, state]) => [key, state.customPrice])
          .reduce((acc, [key, value]) => {
            acc[key as string] = value as number;
            return acc;
        }, {} as Record<string, number>);

        const activeMinOverrides: Record<string, number> = Object.entries(this.editState)
          .filter(([, state]) => state.customMin != null)
          .reduce((acc, [key, state]) => { acc[key] = state.customMin!; return acc; }, {} as Record<string, number>);

        const activeMaxOverrides: Record<string, number> = Object.entries(this.editState)
          .filter(([, state]) => state.customMax != null)
          .reduce((acc, [key, state]) => { acc[key] = state.customMax!; return acc; }, {} as Record<string, number>);

        /**
         * Mode 1 (selectAll): pass excludedIds, query and isSelectAll true.
         * Mode 2 (explicit):  pass entityKeys directly and isSelectAll false.
         */
        let payload: ApplyRecommendationPayload;

        if (this.data.selectAll) {
          payload = {
            entityType: RecommendationEntityType.ITEM,
            type: RecommendationType.PRICE,
            isSelectAll: true,
            excludedKeys: Array.from(this.excludedKeys),
            filter: {
              where: this.buildFilter(),
            },
            customPrices: Object.keys(activeOverrides)?.length ? activeOverrides : undefined,
            customMinPrices: Object.keys(activeMinOverrides).length ? activeMinOverrides : undefined,
            customMaxPrices: Object.keys(activeMaxOverrides).length ? activeMaxOverrides : undefined,
          };
        } else {
          payload = {
            entityType: RecommendationEntityType.ITEM,
            type: RecommendationType.PRICE,
            isSelectAll: false,
            entityKeys: this.selectedItemKeys,
            customPrices: Object.keys(activeOverrides)?.length ? activeOverrides : undefined,
            customMinPrices: Object.keys(activeMinOverrides).length ? activeMinOverrides : undefined,
            customMaxPrices: Object.keys(activeMaxOverrides).length ? activeMaxOverrides : undefined,
          };
        }

        this.recommendationsService
          .apply(payload)
          .pipe(this.autoCleanUp(), first())
          .subscribe(
            () => {
              this.snotifyService.success(`Updated pricing successfully`);
              this.isSubmitting = false;
              this.hasSynced = true;
              this.activeModal.close();
            },
            () => {
              this.isSubmitting = false;
              this.snotifyService.error(`Update pricing failed`);
            }
          );
      }
    });
  }
}
